import { searchLookupOptions } from '../../../api/lookup';
import type { ClarifyFormField } from '../../../types';
import type { FieldValue } from './fields';
import { isPicker } from './fields';

// Kula FormBuilder's field descriptor. The package ships no usable types
// (its .d.ts points at an unshipped ./src), so we pin the shape we rely on.
export interface KulaFieldConfig {
  name: string;
  type: 'input' | 'select' | 'asyncSelect';
  label: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  isMulti?: boolean;
  options?: { value: string; label: string }[];
  fieldProps?: Record<string, unknown>;
}

// Value -> label for everything we've ever seen, so the submit message can
// render a human label for a value picked out of a server search.
export type LabelCache = Map<string, string>;

const seedLabels = (fields: ClarifyFormField[]): LabelCache => {
  const cache: LabelCache = new Map();
  for (const f of fields) {
    for (const o of f.options ?? []) cache.set(o.value, o.label);
  }
  return cache;
};

// A picker starts at its pre-selected option or EMPTY — never options[0].
// Auto-picking the first option silently committed whichever office sorted
// first when the agent's guess had no match in the tenant.
const defaultValue = (field: ClarifyFormField): FieldValue => {
  if (!isPicker(field)) return field.prefill;
  // A restored multi-select answer arrives comma-joined (as the ctx marker
  // stores it); split it back into the chosen values. A single agent guess
  // has no comma, so it stays a one-element array.
  if (field.multi) {
    return field.selected
      ? field.selected
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      : [];
  }
  return field.selected || '';
};

export const buildDefaults = (
  fields: ClarifyFormField[]
): Record<string, FieldValue> =>
  Object.fromEntries(fields.map((f) => [f.key, defaultValue(f)]));

export const buildFormSchema = (
  fields: ClarifyFormField[],
  labels: LabelCache
): KulaFieldConfig[] =>
  fields.map((field) => {
    if (!isPicker(field)) {
      return {
        name: field.key,
        type: 'input',
        label: field.label,
        description: field.help,
        // Backend-authored example text — renders grey, never submits; a
        // required empty field blocks submit (FormBuilder validation).
        placeholder: field.placeholder,
        required: field.required === true,
      };
    }

    const base: KulaFieldConfig = {
      name: field.key,
      type: field.search ? 'asyncSelect' : 'select',
      label: field.label,
      description: field.help,
      // Single pickers must be answered — an empty value reaches the agent as
      // "field=" and creates the record with that field unset.
      required: !field.multi,
      isMulti: field.multi,
      options: field.options ?? [],
      placeholder: `${field.search ? 'Search' : 'Select'} ${field.label
        .toLowerCase()
        .replace(/\?$/, '')}`,
    };

    if (!field.search) return base;

    const search = field.search;
    return {
      ...base,
      fieldProps: {
        selectType: 'async',
        // Select debounces and aborts internally; we just fetch and cache.
        loadOptions: async (input: string) => {
          const opts = await searchLookupOptions(search, input);
          for (const o of opts) labels.set(o.value, o.label);
          return opts;
        },
      },
    };
  });

export { seedLabels };
